import { UpperCasePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { RouterOutlet } from '@angular/router'
import { SwUpdate } from '@angular/service-worker'

import { HlmToaster } from '@yoshu-lib/ui/sonner/src'

import { environment } from '../environments/environment'

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HlmToaster, UpperCasePipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private readonly titleService = inject(Title)
  private readonly swUpdate = inject(SwUpdate)

  readonly currentEnvironment = environment.name
  title = 'demo'

  updateChecked = false
  updateAvailable = false

  get waitingForUpdates(): boolean {
    return !this.updateChecked || this.updateAvailable
  }

  async ngOnInit() {
    if (this.currentEnvironment !== 'production') {
      const currentDocumentTitle = this.titleService.getTitle()
      this.titleService.setTitle(
        `[${this.currentEnvironment.toUpperCase()}] - ${currentDocumentTitle}`
      )
      document.body.classList.add('has-env-banner')
    }

    await this.checkForUpdates()
  }

  private async checkForUpdates(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      console.log('Service worker updates are disabled.')
      this.updateChecked = true
      this.hideLoader()
      return
    }

    this.swUpdate.versionUpdates.subscribe((event) => {
      console.log('Version Updates subscription triggered')

      if (event.type === 'VERSION_DETECTED') {
        console.log('New version detected')
        this.updateAvailable = true
      } else if (event.type === 'VERSION_READY') {
        console.log('Version ready')
        console.log('Old version was', event.currentVersion.hash)
        console.log('New version is', event.latestVersion.hash)
        localStorage.setItem('ngsw_version', event.latestVersion.hash)
        this.swUpdate.activateUpdate().then(() => this.reloadApp())
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.log('Version installation failed')
      } else if (event.type === 'NO_NEW_VERSION_DETECTED') {
        console.log('No new version detected, current version:', event.version.hash)
        localStorage.setItem('ngsw_version', event.version.hash)
      }
    })

    console.log('Service worker enabled, start checking version')

    if (navigator?.serviceWorker?.controller) {
      console.log('Start checkForUpdate')
      await this.swUpdate.checkForUpdate()
      console.log('Finished checkForUpdate')
    } else {
      // First visit - fetch ngsw.json to get initial version
      await this.storeInitialVersion()
    }

    this.updateChecked = true
    this.hideLoader()
  }

  private hideLoader(): void {
    document.body.classList.add('loaded')
  }

  private async storeInitialVersion(): Promise<void> {
    try {
      const response = await fetch('/ngsw.json')
      if (response.ok) {
        const config = await response.json()
        localStorage.setItem('ngsw_version', String(config.timestamp))
        console.log('Initial version stored:', config.timestamp)
      }
    } catch (error) {
      console.log('Could not fetch ngsw.json:', error)
    }
  }

  private reloadApp(): void {
    console.log('The app is updating right now')
    localStorage.setItem('ngsw_updating', 'true')
    document.location.reload()
  }
}
